import { useState } from "react";
import Modal from "@/components/base/Modal";
import { useStudents } from "@/hooks/useStudents";
import { assignStudent } from "@/hooks/useTeacher";

interface AssignStudentModalProps {
  open: boolean;
  onClose: () => void;
  onAssigned: () => void;
  assignedUserIds: Set<string>;
}

// Tüm öğrencileri listeler ve öğretmene atama yapar.
export default function AssignStudentModal({
  open,
  onClose,
  onAssigned,
  assignedUserIds,
}: AssignStudentModalProps) {
  const { data, loading } = useStudents();
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const all = data ?? [];
  const filtered = all.filter((s) => {
    if (assignedUserIds.has(s.userId)) return false;
    if (search && !`${s.name} ${s.email} ${s.grade}`.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    return true;
  });

  const handleAssign = async (userId: string) => {
    setBusyId(userId);
    setError("");
    const res = await assignStudent(userId);
    setBusyId(null);
    if (res.ok) {
      onAssigned();
    } else {
      setError(res.message || "Atama sırasında bir hata oluştu.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Öğrenci Ata"
      titleId="assign-student-title"
      id="assign-student-modal"
      maxWidthClass="max-w-lg"
    >
      <div className="space-y-4">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" aria-hidden="true"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Öğrenci ara (ad, e-posta, sınıf)..."
            className="w-full h-10 pl-9 pr-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none transition-colors"
          />
        </div>

        {error && (
          <div className="rounded-md bg-accent-100/70 text-accent-800 px-3 py-2.5 text-sm">{error}</div>
        )}

        {loading ? (
          <p className="flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
            Öğrenciler yükleniyor...
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-foreground-500">
            Atanabilecek öğrenci bulunamadı. Tüm öğrenciler zaten atanmış olabilir.
          </p>
        ) : (
          <ul className="divide-y divide-background-200 max-h-[50svh] overflow-y-auto">
            {filtered.map((s) => (
              <li key={s.userId} className="flex items-center gap-3 py-2.5">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 text-xs font-semibold shrink-0">
                  {s.name.trim().charAt(0).toLocaleUpperCase("tr-TR")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-900 truncate">{s.name}</p>
                  <p className="text-xs text-foreground-500 truncate">
                    {s.grade ? `${s.grade} · ` : ""}
                    {s.email}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleAssign(s.userId)}
                  disabled={busyId === s.userId}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-primary-500 text-background-50 whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 transition-colors"
                >
                  {busyId === s.userId ? (
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                  ) : (
                    <i className="ri-user-add-line" aria-hidden="true"></i>
                  )}
                  Ata
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}